import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Input,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  tableCellClasses,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Close, Download } from "@mui/icons-material";

import { RootState } from "../../../redux/store";
import { getOrgInfo } from "../../../redux/features/organization/organization-slice";
import {
  getPlans,
  getTenantUsage,
  getTenantPayments,
  getTenantPlan,
  UpdateSubscription,
  getAddons,
} from "../../../redux/features/plan-billing/plan-billing-slice";

import {
  IStripeProduct,
  ITenantUsage,
  IPayment,
  ICurrentPlan,
} from "../../../types/product";

import "./myplan.scss";
import { current } from "node_modules/@reduxjs/toolkit/dist";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  padding: "5px 20px",
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#ECF6FF",
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
    height: "40px",
  },

  "&:last-child td, &:last-child th": {
    border: 0,
  },
  height: "50px",
}));

const StyledTable = styled(Table)(() => ({
  [`& .${tableCellClasses.root}`]: {
    borderBottom: "none",
  },
}));

const MyPlan = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialLoadDone = useRef(false);
  // const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  // const [totalCost, setTotalCost] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  const products = useSelector<RootState, IStripeProduct[]>(
    (state) => state.planBillingSlice.products
  );
  const textProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.textProducts
  );
  const mailProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.mailProducts
  );
  const scheduleProducts = useSelector(
    (state: RootState) => state.planBillingSlice.addons.scheduleProducts
  );

  //-------------------------------------------------------------------------
  //tenant specific 
  // const orgData = useSelector(
  //   (state: RootState) => state.organizationSlice.orgData
  // );
  const tenantUsage = useSelector<RootState, ITenantUsage>(
    (state) => state.planBillingSlice.tenantUsage
  );
  const payments = useSelector<RootState, IPayment[]>(
    (state) => state.planBillingSlice.payments
  );
  const currentPlan = useSelector<RootState, ICurrentPlan>(
    (state) => state.planBillingSlice.currentPlan
  );

  const [updateAdmin, setUpdateAdmin] = useState(false);
  const [adminSeats, setAdminSeats] = useState<number>(0);
  const [mailSeatCount, setMailSeatCount] = useState<number>(0);
  const [smsPriceId, setSmsPriceId] = useState<string | null>(null);
  const [schedSelected, setSchedSelected] = useState<boolean>(false);

  const loadAllData = async () => {
    // console.log("[myPlan] Starting loadAllData...");
    setIsLoading(true);
    
    try {
      // First get fresh org data to ensure we have the latest subscription ID
      await dispatch(getOrgInfo({ useLoader: true }));
      // console.log("[myPlan] Org info loaded");
      
      // Then load all plan related data
      await Promise.all([
        dispatch(getPlans()),
        dispatch(getAddons()),
        dispatch(getTenantPlan()),
        dispatch(getTenantUsage()),
        dispatch(getTenantPayments())
      ]);

      // console.log("[myPlan] All data loaded successfully");
      initialLoadDone.current = true;
    } catch (error) {
      console.error("[myPlan] Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Prevent concurrent loads
    if (initialLoadDone.current || isLoading) return;
    loadAllData();
  }, []); // Remove dispatch dependency since it's stable in React

  // Separate useEffect for handling state updates based on currentPlan changes
  useEffect(() => {
    if (currentPlan.currentPlanId !== "" && currentPlan.adminSeats > 0) {
      setAdminSeats(currentPlan.adminSeats);
    }

    if (currentPlan.addons?.mailbox) {
      setMailSeatCount(currentPlan.addons.mailbox.quantity);
    }
    if (currentPlan.addons?.sms) {
      setSmsPriceId(currentPlan.addons.sms.priceId);
    }
    if (currentPlan.addons?.scheduling) {
      setSchedSelected(true);
    }
  }, [currentPlan]);

  // console.log("currentPlan: ", currentPlan);
  // console.log("payment: ", payments);
  // console.log("mailSeatCount, smsPriceId, schedSelected: ", mailSeatCount, smsPriceId, schedSelected);

  // const calculateAddOnTotal = () => {
  //   let total = 0;
  //   const mailProduct = mailProducts[0];
  //   const schedProduct = scheduleProducts[0];
  //   const selectedSmsProduct = textProducts.find(
  //     (p: any) => p.priceId === smsPriceId
  //   );

  //   if (mailSeatCount && mailProduct?.price) {
  //     total += mailSeatCount * mailProduct.price;
  //   }
  //   if (schedSelected && schedProduct?.price) {
  //     total += schedProduct.price;
  //   }
  //   if (selectedSmsProduct?.price) {
  //     total += selectedSmsProduct.price;
  //   }

  //   return total;
  // };

  // const handleAddonUpdate = () => {
  //   const total = calculateAddOnTotal();
  //   setTotalCost(total);
  //   setShowConfirmDialog(true); // open dialog first
  // };

  // console.log("tenantUsage,currentPlan: ", tenantUsage, currentPlan);

const goToViewPlan = (addonPage: boolean) => {
  const selectedAddons: any = {};

  
  if (currentPlan?.addons?.mailbox) {
    selectedAddons.mailbox = {
      priceId: currentPlan?.addons?.mailbox?.priceId,
      quantity: currentPlan?.addons?.mailbox?.quantity,
    };
  }

  if (currentPlan?.addons?.sms) {
    selectedAddons.sms = {
      priceId: currentPlan?.addons?.sms?.priceId,
    };
  }
  
 if (currentPlan?.addons?.scheduling) {
    selectedAddons.scheduling = {
      priceId: currentPlan?.addons?.scheduling?.priceId,
    };
  }

  // console.log("my-plan selectedAddons: ", addonPage, selectedAddons);

  navigate("/view-plan", {
    state: {
      selectedPlan: currentPlan, //selected plan = current plan!
      addOnPage: addonPage,  //indicate this from update-addons
      selectedAddons,
      adminSeats,
      mailBoxSeats: mailSeatCount,
      mailAddCheck: !!selectedAddons.mailbox?.priceId,
      textAddCheck: !!selectedAddons.sms?.priceId,
      scheduleAddCheck: !!selectedAddons.scheduling?.priceId,
    },
  });
};

// console.log("currentPlan: ", currentPlan);
// console.log("tenantUsage: ", tenantUsage);
// console.log("mailSeatCount: ", mailSeatCount);
// console.log("payments: ", payments);

  return (
    <>
      <div className="pageTemplate">
        <Grid container spacing={3}>
          <Grid display="flex" gap="15px" size={{ xs: 12 }}>
            <Typography variant="h6" className="DetailsPageTitle">
              My Plan
            </Typography>
            <div className="userPlanCard__footer">
              <Typography
                variant="h6"
                className="viewAllPlan"
                onClick={() => goToViewPlan(false)}
              >
                View All Plans
              </Typography>
            </div>
          </Grid>
          {/* Main Plan */}
          <Grid size={{ xs: 12, md: 8, lg: 4 }}>
            <div className="userPlanCard">
              <div className="userPlanCard__head">
                <span className="userPlanCard__body-row-title">
                  Main Plan
                </span>
                <Typography className="userPlanCard__title">
                  {currentPlan.currentPlanId !== ""
                    ? currentPlan?.planDetail?.title
                    : "not active yet"}
                </Typography>
                <strong>
                  {currentPlan.currentPlanId !== "" &&
                  currentPlan.planDetail?.price > 0
                    ? `$ ${currentPlan.planDetail?.price.toLocaleString()} /mo`
                    : `- `}
                </strong>
                {/* <button className="userPlanCard__head-btn">
                  {currentPlan.currentPlanId !== "" ? "Active" : "no plan yet"}
                </button> */}
                <Button
                  className="userPlanCard__footer-btn"
                  onClick={() => goToViewPlan(false)}
                >
                  Change
                </Button>
              </div>
              {/* <div className="userPlanCard__footer">
                <div className="userPlanCard__price">
                  
                </div>
               
              </div> */}

              {/* Admin Seats */}
              <div className="userPlanCard__footer">
                <Box display="flex" alignItems="center" gap={2}>
                  <strong>
                    <Typography fontSize={"20px"}>Total Admin Seats</Typography>
                  </strong>
                  <button className="userPlanCard__head-btn">
                    <strong style={{ fontSize: "18px" }}>
                      {currentPlan?.adminSeats > 0
                        ? `${currentPlan?.adminSeats}`
                        : `-`}
                    </strong>{" "}
                  </button>
                </Box>
                
                <div className="userPlanCard__footer">
                  <Typography
                    variant="h6"
                    className="viewAllPlan"
                    onClick={() => setUpdateAdmin(true)}
                  >
                    Change Seats
                  </Typography>
                </div>
              </div>
              
              {/* Next Invoice */}
              <div className="userPlanCard__footer">
                <Box display="flex" alignItems="center" gap={2}>
                  <span className="userPlanCard__body-row-title">
                    Next Invoice Due
                  </span>
                  <span>
                    {currentPlan.subscription && currentPlan.subscription.current_period_end
                      ? new Date(currentPlan.subscription.current_period_end * 1000).toLocaleDateString()
                      : `-`}
                  </span>
                </Box>
                <div className="userPlanCard__price">
                  <span>
                    {currentPlan.currentPlanId !== "" && currentPlan.amount_due > 0
                      ? `$ ${(currentPlan.amount_due / 100).toLocaleString()} `
                      : `- `}
                  </span>
                </div>
              </div>
              
            </div>
          </Grid>
          {/* Add-ons */}
          <Grid size={{ xs: 12, md: 8, lg: 4 }}>
            <div className="userPlanCard">
              <div className="userPlanCard__head">
                <Typography className="userPlanCard__title">
                  Add-On
                </Typography>
                <Button
                  className="userPlanCard__footer-btn"
                  onClick={() => {
                    if (currentPlan?.currentPlanId === null) {
                      goToViewPlan(false);
                    } else {
                      goToViewPlan(true)}
                    }
                  }
                >
                  {currentPlan && 
                  (currentPlan.addons?.mailbox?.priceId || 
                  currentPlan.addons?.sms?.priceId || 
                  currentPlan.addons?.scheduling?.priceId)
                    ? "Change"
                    : "Add"}
                </Button>
              </div>

              {/* Mailbox Add-on */}
              <div className="userPlanCard__footer">
                <span className="userPlanCard__body-row-title">
                  Gmail/Outlook Integration
                </span>
                {currentPlan?.addons.mailbox?.priceId ? (
                  <Box display={"flex"} alignItems={"center"} gap={"10px"}>
                    <button className="userPlanCard__head-btn">
                    <strong style={{ fontSize: "18px" }}>
                      {mailSeatCount} 
                    </strong>
                    </button>
                    <Typography fontSize={"18px"}> mailboxes</Typography>
                    {/* <Input
                      sx={{ width: "40px" }}
                      inputProps={{
                        sx: {
                          textAlign: "center",
                        },
                        min: 1,
                        type: "number",
                      }}
                      value={mailSeatCount}
                      onChange={(e) => setMailSeatCount(parseInt(e.target.value))}
                      // inputProps={{ min: 1 }}
                    ></Input> */}
                    {/* <Typography fontSize={"18px"}>mailboxes</Typography> */}
                  </Box>
                ) : (
                  <span>not active</span>
                )}
              </div>

              {/* SMS Add-on */}
              <div className="userPlanCard__footer">
                <span className="userPlanCard__body-row-title">
                  SMS Integration
                </span>
                {currentPlan?.addons.sms?.priceId ? (
                  <Box display={"flex"} alignItems={"center"} gap={"10px"}>
                    <Select
                      value={smsPriceId || ""}
                      onChange={(e) => setSmsPriceId(e.target.value)}
                    >
                      {textProducts.map((p: any) => (
                        <MenuItem key={p.priceId} value={p.priceId}>
                          {p.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                ) : (
                  <span>not active</span>
                )}
              </div>

              {/* Scheduling Add-on */}
              <div className="userPlanCard__footer">
                <span className="userPlanCard__body-row-title">
                  Scheduling
                </span>
                <span>
                  {currentPlan?.addons?.scheduling?.priceId
                    // don't show amount, since we will have to address discount!!
                    // ? `$${
                    //     scheduleProducts.find(
                    //       (p: any) => p.priceId === currentPlan?.addons?.scheduling?.priceId
                    //     )?.price || "0.00"
                    //   }`
                    ? "active"
                    : "not active"}
                </span>
              </div>
              
            </div>
          </Grid>


          {/* Current Usage */}
          <Grid size={{ xs: 12, md: 8, lg: 4 }}>
            <div className="userPlanCard">
              <div className="userPlanCard__head">
                <Typography className="userPlanCard__title">
                  Current Usage
                </Typography>
              </div>
              <div className="userPlanCard__body">
                <div className="userPlanCard__body-row">
                  <span className="userPlanCard__body-row-title">Admins</span>
                  <span className="userPlanCard__body-row-count">
                    {currentPlan && currentPlan.planDetail?.adminCovered > 0
                      ? `${tenantUsage.adminCount} / ${currentPlan.adminSeats}`
                      : `${tenantUsage.adminCount} / no plan yet`}
                  </span>
                </div>
                <div className="userPlanCard__body-row">
                  <span className="userPlanCard__body-row-title">
                    Client Count
                  </span>
                  <span className="userPlanCard__body-row-count">
                    {currentPlan && currentPlan.planDetail?.clientLimit > 0
                      ? `${tenantUsage.clientCount} /
                    ${currentPlan.planDetail?.clientLimit}`
                      : `${tenantUsage.clientCount} / no plan yet`}
                  </span>
                </div>
                <div className="userPlanCard__body-row">
                  <span className="userPlanCard__body-row-title">
                    Employee Count
                  </span>
                  <span className="userPlanCard__body-row-count">
                    {tenantUsage.providerCount}
                  </span>
                </div>
              </div>
            </div>
          </Grid>

          {/* Payment / Invoices */}
          <Grid size={{ xs: 12 }} className="listingTable">
            <Typography
              variant="h6"
              className="DetailsPageTitle"
              marginTop={"25px"}
            >
              Payments
            </Typography>
            <TableContainer className="tableResponsive">
              <StyledTable
                sx={{
                  minWidth: 820,
                  borderCollapse: "separate",
                  cursor: "pointer",
                }}
                aria-label="customized table"
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Invoice ID</StyledTableCell>
                    <StyledTableCell>Plan</StyledTableCell>
                    {/* <StyledTableCell>Cycle Start</StyledTableCell> */}
                    <StyledTableCell>Invoice Date</StyledTableCell>
                    <StyledTableCell>Total Amount</StyledTableCell>
                    <StyledTableCell>Credit Applied</StyledTableCell>
                    <StyledTableCell>Paid Amount</StyledTableCell>
                    {/* <StyledTableCell>Payment Method</StyledTableCell> */}
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Download</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments?.length > 0 && payments?.map((item, index) => (
                    <StyledTableRow style={{ borderRadius: 20 }} key={index}>
                      <StyledTableCell component="th">
                        {item.invoiceNumber}
                      </StyledTableCell>
                      <StyledTableCell component="th">
                        {products.find(
                          (product: IStripeProduct) =>
                            product.productId === item.productId
                        )?.title || ""}
                      </StyledTableCell>
                      {/* <StyledTableCell component="th">
                        {item.invoiceCreateDate ? new Date(item.invoiceCreateDate).toLocaleDateString() : '-'}
                      </StyledTableCell> */}
                      <StyledTableCell component="th">
                        {item.invoiceDueDate ? new Date(item.invoiceDueDate).toLocaleDateString() : '-'}
                      </StyledTableCell>
                      <StyledTableCell component="th">
                        {item.invoiceTotalAmount < 0
                          ? `-$${Math.abs(item.invoiceTotalAmount / 100)}`
                          : `$${item.invoiceTotalAmount / 100}`}
                      </StyledTableCell>
                      <StyledTableCell component="th">
                        {item.invoiceCreditApplied < 0
                          ? `-$${Math.abs(item.invoiceCreditApplied / 100)}`
                          : `$${item.invoiceCreditApplied / 100}`}
                      </StyledTableCell>
                      <StyledTableCell component="th">
                        {item.invoiceAmountPaid < 0
                          ? `-$${Math.abs(item.invoiceAmountPaid / 100)}`
                          : `$${item.invoiceAmountPaid / 100}`}
                      </StyledTableCell>
                      {/* <StyledTableCell component="th">
                        Credit Card
                      </StyledTableCell> */}
                      <StyledTableCell component="th">
                        <Box className="statusChip">{item.invoiceStatus}</Box>
                      </StyledTableCell>
                      <StyledTableCell component="th">
                        <a href={item.pdfUrl}>
                          <IconButton style={{ background: "transparent" }}>
                            <Download />
                          </IconButton>
                        </a>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </StyledTable>
            </TableContainer>
          </Grid>
        </Grid>
      </div>

      {/* <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        className="smallModel upgradeModal"
      >
        <div>
          <Grid>
            <Box className="largeModel__head">
              <Typography variant="h6">Confirm Add-On Update</Typography>
            </Box>
          </Grid>
        </div>
        <DialogContent>
          <Typography mt={2}>
            The total cost for your selected add-ons is{" "}
            <strong>${totalCost}</strong>/mo. This amount will be{" "}
            <strong>automatically charged</strong> to your saved card.
          </Typography>
          <Typography mt={1} color="text.secondary">
            Do you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            variant="contained"
            className="border-button"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmAddonUpdate}
            color="primary"
            variant="contained"
          >
            Confirm & Update
          </Button>
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={updateAdmin}
        // onClose={() => setUpdateAdmin(false)}
        className="smallModel upgradeModal"
      >
        <div>
          <Grid>
            <Box className="largeModel__head">
              <Typography variant="h5" className="largeModel__title">
                Update Admin Seats
              </Typography>
              <Button
                className="largeModel__closeBtn"
                onClick={() => {
                  setUpdateAdmin(false);
                }}
              >
                <Close />
              </Button>
            </Box>
          </Grid>

          <DialogContent>
            <Box>
              You current plan is{" "}
              <b>{`${currentPlan?.planDetail?.title}`} Plan. </b>$
              {currentPlan?.planDetail?.price} / month, up to{" "}
              {currentPlan?.planDetail?.clientLimit} clients,{" "}
              {currentPlan?.planDetail?.adminCovered === 1
                ? "1 admin seat"
                : currentPlan?.planDetail?.adminCovered + " admin seats"}
              . You can add more admin seats if you want.
              <p></p>
            </Box>

            <Box mt={1}>
              <TextField
                value={adminSeats}
                onChange={(e) => {
                  const count = parseInt(e.target.value);

                  // exclude enterprise plan checking
                  if (
                    currentPlan && currentPlan.planDetail?.title !== "Enterprise" &&
                    count < currentPlan?.planDetail?.adminCovered
                  )
                    return;
                  setAdminSeats(count);
                }}
                label="Total Admin Seats"
                variant="outlined"
                type="number"
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              className="border-button"
              variant="contained"
              onClick={() => setUpdateAdmin(false)}
            >
              Cancel
            </Button>
            <Button
              className="button"
              variant="contained"
              onClick={async () => {
                //update admin seats
                await dispatch(
                  UpdateSubscription({
                    productId: currentPlan?.planDetail?.productId,
                    adminSeats: adminSeats,
                    updateMode: "update-adminSeats", //must match backend!!
                  })
                );
                await loadAllData(); // Refresh all data after update
                setUpdateAdmin(false);
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </>
  );
};

export default MyPlan;
